import { View, TouchableOpacity, ScrollView } from "react-native";
import Svg, { Circle } from "react-native-svg";
import LinearGradient from "react-native-linear-gradient";
import { Icon as RawIcon } from "@ant-design/react-native";
import Icon from "@shared/components/md-design-system/Icon/Icon";
import { HeartIcon } from "@shared/components/md-design-system/Icon/ActivityIcon";
import { Head } from "@shared/components/head";
import { Text } from "@shared/components/text";
import Button from "@shared/components/md-design-system/Button/Button";
import { colorPalette } from "@shared/config/colorsConfig";
import { CenteredModal } from "@shared/components/CenteredModal";
import { dynamicTranslations } from "@shared/utils/dynamicTranslation";

interface StepsModalProps {
    visible: boolean;
    onClose: () => void;
    current: number;
    goal?: number;
}

const RING_RADIUS = 33;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const POINTS = [
    { key: 'heart' },
    { key: 'energy' },
] as const;

export const StepsModal = ({ visible, onClose, current, goal }: StepsModalProps) => {
    const { t } = dynamicTranslations();
    const hasGoal = !!goal && goal > 0;
    const percentage = hasGoal ? Math.min(100, Math.round((current / goal!) * 100)) : 0;
    const remaining = hasGoal ? Math.max(0, goal! - current) : 0;
    const reached = hasGoal && current >= goal!;
    const dashOffset = RING_CIRCUMFERENCE * (1 - percentage / 100);
    const formatNumber = (n: number) => new Intl.NumberFormat().format(n);

    return (
        <CenteredModal visible={visible} onClose={onClose}>
            <View className="w-full bg-white rounded-[22px] max-h-[85%]">
                <TouchableOpacity
                    onPress={onClose}
                    className="absolute right-3 top-3 w-12 h-12 rounded-full bg-white border border-gray-300 items-center justify-center z-10"
                >
                    <RawIcon name="close" size={18} color={colorPalette.gray[500]} />
                </TouchableOpacity>

                <View className="m-2">
                    <LinearGradient
                        colors={['#0C3963', '#072742']}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 1 }}
                        style={{ borderRadius: 16, flexDirection: 'row', alignItems: 'center', gap: 20, padding: 24 }}
                    >
                        <View style={{ width: 88, height: 88 }} className="items-center justify-center">
                            <View style={{ transform: [{ rotate: '-90deg' }] }}>
                                <Svg width={88} height={88} viewBox="0 0 76 76">
                                    <Circle cx={38} cy={38} r={RING_RADIUS} stroke="rgba(255,255,255,0.18)" strokeWidth={7} fill="none" />
                                    <Circle
                                        cx={38}
                                        cy={38}
                                        r={RING_RADIUS}
                                        stroke={colorPalette.secondary[700]}
                                        strokeWidth={7}
                                        strokeLinecap="round"
                                        fill="none"
                                        strokeDasharray={RING_CIRCUMFERENCE}
                                        strokeDashoffset={dashOffset}
                                    />
                                </Svg>
                            </View>
                            <Text className="absolute font-heading font-bold text-white text-base">{`${percentage}%`}</Text>
                        </View>

                        <View className="flex-1">
                            <Text className="font-heading font-bold text-white text-4xl">{formatNumber(current)}</Text>
                            <Text className="text-base mt-1" style={{ color: 'rgba(255,255,255,0.78)' }}>
                                {t('screens.activities.steps.subLabel', { goal: hasGoal ? formatNumber(goal!) : '--' })}
                            </Text>
                        </View>
                    </LinearGradient>
                </View>

                <ScrollView className="px-7" contentContainerStyle={{ paddingBottom: 24 }}>
                    <Head type="h2" className="mb-2">
                        {reached
                            ? t('screens.activities.steps.reachedTitle')
                            : t('screens.activities.steps.remainingTitle', { count: formatNumber(remaining) })}
                    </Head>
                    <Text className="text-base text-black mb-5">
                        {reached ? t('screens.activities.steps.reachedLead') : t('screens.activities.steps.remainingLead')}
                    </Text>

                    <View className="flex-col gap-4 mb-2">
                        {POINTS.map(({ key }) => (
                            <View key={key} className="flex-row items-start gap-4">
                                <View className="w-12 h-12 rounded-xl bg-secondary-100 items-center justify-center shrink-0">
                                    {key === 'heart' ? (
                                        <HeartIcon size={22} color={colorPalette.secondary[600]} />
                                    ) : (
                                        <Icon name="check-circle" size={22} color={colorPalette.secondary[600]} />
                                    )}
                                </View>
                                <View className="flex-1">
                                    <Text className="font-semibold text-base">
                                        {t(`screens.activities.steps.points.${key}.title`)}
                                    </Text>
                                    <Text className="text-md text-gray-500 mt-1">
                                        {t(`screens.activities.steps.points.${key}.description`)}
                                    </Text>
                                </View>
                            </View>
                        ))}
                    </View>

                    <View className="mt-2">
                        <Button size="large" variant="primary" onPress={onClose}>
                            {t('screens.activities.steps.gotIt')}
                        </Button>
                    </View>
                </ScrollView>
            </View>
        </CenteredModal>
    );
};

export default StepsModal;
